import { doc, getDoc, onSnapshot, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { User } from 'firebase/auth';
import { db } from './firebase';
import { UserProfile, SubscriptionPlan } from './types';
import { captureAttribution } from './attribution';
import { currentMarket } from './market';

const COLLECTION_NAME = 'userProfiles';

const DEFAULT_PLAN: SubscriptionPlan = 'free';
const DEFAULT_CREDITS = 1;

/**
 * Creates a user profile document if one doesn't already exist.
 * Called after sign-up or first sign-in (e.g. Google OAuth).
 *
 * On first creation, the first-touch marketing/path attribution captured on
 * the landing page (or on direct-to-app arrival) is persisted onto the profile.
 */
export async function createUserProfileIfNotExists(user: User): Promise<boolean> {
  const docRef = doc(db, COLLECTION_NAME, user.uid);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    const attribution = captureAttribution();
    await setDoc(docRef, {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      subscriptionPlan: DEFAULT_PLAN,
      creditsRemaining: DEFAULT_CREDITS,
      // Picks the language of lifecycle emails (welcome, post-purchase)
      locale: navigator.language || null,
      // Picks the branding and links of those emails (callout.jp or heycallout.com)
      market: currentMarket().id,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...(Object.keys(attribution).length > 0 ? { attribution } : {}),
    });
    return true;
  }
  return false;
}

/**
 * Fetches a user profile by UID. Returns null if not found.
 */
export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  const docRef = doc(db, COLLECTION_NAME, uid);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data() as UserProfile;
  }
  return null;
}

/**
 * Calls `onChange` with the user's profile now and whenever it changes (null
 * if there is none), so credits the payment webhook adds after a purchase show
 * without a reload. Returns the function that stops listening.
 */
export function subscribeToUserProfile(
  uid: string,
  onChange: (profile: UserProfile | null) => void
): () => void {
  return onSnapshot(
    doc(db, COLLECTION_NAME, uid),
    (docSnap) => onChange(docSnap.exists() ? (docSnap.data() as UserProfile) : null),
    (err) => console.error('User profile listener failed:', err)
  );
}

/**
 * Updates the client-editable fields of an existing user profile.
 * Plan and credits are backend-only; firestore.rules rejects client writes to them.
 */
export async function updateUserProfile(
  uid: string,
  updates: Partial<Pick<UserProfile, 'displayName'>>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, uid);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}
